import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { ReportFrame, ReportSummaryStrip, reportMeta } from "./Frame";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Money, plural, relativeDay } from "../kit";
import { cn } from "@/lib/utils";

/**
 * Report — Warehouse low stock. "What do I need to reorder?"
 *
 * The reorder list. Worst first, because the top of the list is the thing that
 * gets ordered and the bottom of the list is the thing that gets scrolled past.
 *
 * "Below minimum" here is the server's rule, and the server's rule is the same
 * comparison that fires the low-stock alert on dispatch (`onHand <
 * minStockLevel`, warehouse row). The footnote says so out loud, so nobody has
 * to reconcile this screen against the alert list in front of the client.
 *
 * 390px first: the four numbers a reorder decision needs (on hand, reserved,
 * available, minimum) wrap inside the item cell rather than becoming four more
 * columns — a six-column table is the one shape this report cannot take on a
 * phone, and it is the reason the figures live in the cell that wraps.
 */
export function LowStock() {
  const data = useQuery(api.reports.lowStock.data);

  return (
    <ReportFrame
      {...reportMeta("low-stock")}
      question="What do I need to reorder?"
      data={data}
      count={data?.count}
      empty={{
        title: "Everything is above its minimum",
        body: "No warehouse item is under its minimum stock level. Nothing to reorder.",
        good: true,
      }}
      footnote={
        data ? (
          <>
            Below minimum means warehouse on-hand is under the item's minimum
            stock level — the same rule that fires the low-stock alert.{" "}
            {data.count} of {data.warehouseItems} warehouse items.
            {data.pricePendingCount > 0
              ? ` Top-up cost excludes ${data.pricePendingCount} ${plural(
                  data.pricePendingCount,
                  "item",
                )} with no price on the sheet.`
              : ""}
          </>
        ) : null
      }
    >
      {data ? (
        <>
          <ReportSummaryStrip>
            <span className="num font-semibold">
              {data.count} {plural(data.count, "item")} to reorder
            </span>
            <span className="num">
              {data.totalShortfallUnits}{" "}
              {plural(data.totalShortfallUnits, "unit")} short
            </span>
            <span className="num">
              <Money cents={data.totalTopUpCostCents} /> to top up
            </span>
          </ReportSummaryStrip>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead className="text-right">Short</TableHead>
                <TableHead className="text-right">Top-up</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((row) => (
                <TableRow key={row.itemId}>
                  <TableCell className="whitespace-normal">
                    <div className="font-medium">{row.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {row.vendor} · {row.vendorItemNumber} · {row.packSize}
                    </div>
                    {/* The reorder numbers. Wraps at 390px. */}
                    <div className="num mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                      <span>
                        on hand{" "}
                        <span
                          className={
                            row.onHand === 0
                              ? "font-medium text-red-700"
                              : "font-medium text-foreground"
                          }
                        >
                          {row.onHand}
                        </span>
                      </span>
                      <span>
                        reserved{" "}
                        <span className="font-medium text-foreground">
                          {row.reserved}
                        </span>
                      </span>
                      <span>
                        available{" "}
                        <span
                          className={
                            row.available === 0
                              ? "font-medium text-red-700"
                              : "font-medium text-foreground"
                          }
                        >
                          {row.available}
                        </span>
                      </span>
                      <span>
                        min{" "}
                        <span className="font-medium text-foreground">
                          {row.minStockLevel}
                        </span>
                      </span>
                      <span>counted {relativeDay(row.updatedAt)}</span>
                    </div>
                  </TableCell>

                  {/* Out of stock is red — somebody must act today. Merely
                      below minimum is amber, the urgency colour, because it is
                      a qualifier on a stock level rather than a status. */}
                  <TableCell
                    className={cn(
                      "num text-right align-top font-semibold",
                      row.outOfStock ? "text-red-700" : "text-amber-700",
                    )}
                  >
                    {row.shortfall}
                  </TableCell>
                  <TableCell className="num text-right align-top text-muted-foreground">
                    {row.topUpCostCents === null ? (
                      "pending"
                    ) : (
                      <Money cents={row.topUpCostCents} />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      ) : null}
    </ReportFrame>
  );
}
